// The first device chooses who holds the account key (D59, docs/07 "Managed account keys"):
// this browser (self-managed: the recovery file flow, the platform cannot read
// conversations) or the platform (managed: no recovery file, new devices read old
// conversations, and the platform can read them too). The warning is plain on purpose.
import { useState } from "react";

import { useT } from "../i18n";
import type { Proto } from "../proto/index";
import { PanelNote, PanelPrimary, PanelSecondary } from "./AccountPanel";
import { CreateAccountKey } from "./CreateAccountKey";
import { createManagedAccountKey } from "./setup";

interface Props {
  proto: Proto;
  deviceId: string;
  onDone: () => void;
  onError: (message: string) => void;
}

type Choice = "undecided" | "self" | "managed";

export function ChooseKeyMode({ proto, deviceId, onDone, onError }: Props) {
  const { account, common } = useT();
  const t = account.choose;
  const [choice, setChoice] = useState<Choice>("undecided");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (choice === "self") return <CreateAccountKey proto={proto} deviceId={deviceId} onDone={onDone} onError={onError} />;

  function onManaged() {
    setBusy(true);
    // Refresh only after success: a refresh re-renders the chooser and would wipe the error
    // the user needs to see (found 2026-10-08: a failed hand-over looked like nothing happened).
    setError(null);
    createManagedAccountKey(proto, deviceId)
      .then(onDone)
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
        onError(message);
      })
      .finally(() => setBusy(false));
  }

  if (choice === "managed") {
    return (
      <div role="group" aria-label={t.managedLabel}>
        <PanelNote>
          <span className="block font-semibold text-white">{t.managedStrong}</span>
          {t.managedNote}
        </PanelNote>
        <div className="mt-5 flex flex-wrap gap-2.5">
          <PanelPrimary disabled={busy} onClick={onManaged}>
            {busy ? t.handingOver : t.letPlatform}
          </PanelPrimary>
          <PanelSecondary disabled={busy} onClick={() => setChoice("undecided")}>
            {common.actions.back}
          </PanelSecondary>
        </div>
        {error ? (
          <p role="alert" className="mt-3 rounded-md bg-red/15 px-3 py-2 text-[12.5px] text-white">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div role="group" aria-label={t.label}>
      <PanelNote>
        <span className="block font-semibold text-white">{t.question}</span>
        {t.questionNote}
      </PanelNote>
      <ul className="mt-4 space-y-2 text-[12.5px] leading-relaxed text-panel-foreground/80">
        <li>
          <span className="font-semibold text-white">{t.myDevices}</span>
          {t.myDevicesNote}
        </li>
        <li>
          <span className="font-semibold text-white">{t.platform}</span>
          {t.platformNote}
        </li>
      </ul>
      <div className="mt-5 flex flex-wrap gap-2.5">
        <PanelPrimary onClick={() => setChoice("self")}>{t.keepOnMyDevices}</PanelPrimary>
        <PanelSecondary onClick={() => setChoice("managed")}>{t.letPlatform}</PanelSecondary>
      </div>
    </div>
  );
}
